import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { getPublicSiteUrl } from '@/lib/domain'

// Default theme configuration for the Next.js viewer
const DEFAULT_THEME = {
  slug: 'default',
  name: 'Default Theme',
  description: 'Professional limousine service theme with elegant dark styling',
  defaults: {
    primary_color: '#C9A227',
    secondary_color: '#1A1A1A',
    accent_color: '#FFFFFF',
  },
}

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import {
  Loader2,
  Globe,
  Copy,
  Check,
  ExternalLink,
  Palette,
  Search,
  RotateCcw,
  Code2,
  AlertTriangle,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

// Setting keys for website configuration
const WEBSITE_SETTINGS = {
  ENABLED: 'website_enabled',
  THEME_ID: 'website_theme_id',
  PRIMARY_COLOR: 'website_primary_color',
  SECONDARY_COLOR: 'website_secondary_color',
  ACCENT_COLOR: 'website_accent_color',
  META_TITLE: 'seo_meta_title',
  META_DESCRIPTION: 'seo_meta_description',
  GOOGLE_ANALYTICS_ID: 'seo_google_analytics_id',
  CUSTOM_HEAD_SCRIPTS: 'website_custom_head_scripts',
  CUSTOM_BODY_SCRIPTS: 'website_custom_body_scripts',
} as const

// Zod schema for the form
const websiteSettingsSchema = z.object({
  website_enabled: z.boolean().default(false),
  website_theme_id: z.string().optional(),
  website_primary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color')
    .optional()
    .or(z.literal('')),
  website_secondary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color')
    .optional()
    .or(z.literal('')),
  website_accent_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color')
    .optional()
    .or(z.literal('')),
  seo_meta_title: z.string().max(70, 'Max 70 characters').optional(),
  seo_meta_description: z.string().max(160, 'Max 160 characters').optional(),
  seo_google_analytics_id: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true
        // GA4 format: G-XXXXXXXXXX
        return /^G-[A-Z0-9]+$/i.test(val)
      },
      { message: 'Invalid Google Analytics ID (e.g., G-XXXXXXXXXX)' }
    ),
  website_custom_head_scripts: z.string().optional(),
  website_custom_body_scripts: z.string().optional(),
})

type WebsiteSettingsFormData = z.infer<typeof websiteSettingsSchema>

export function WebsitePage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)

  const activeAccount = isImpersonating ? impersonatedAccount : account
  const publicSiteUrl = activeAccount?.slug
    ? getPublicSiteUrl(activeAccount.slug)
    : ''

  // Single default theme for Next.js viewer (theme system simplified)
  const themes = [DEFAULT_THEME]

  // Fetch account settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form setup
  const form = useForm<WebsiteSettingsFormData>({
    resolver: zodResolver(websiteSettingsSchema),
    defaultValues: {
      website_enabled: false,
      website_theme_id: '',
      website_primary_color: '',
      website_secondary_color: '',
      website_accent_color: '',
      seo_meta_title: '',
      seo_meta_description: '',
      seo_google_analytics_id: '',
      website_custom_head_scripts: '',
      website_custom_body_scripts: '',
    },
  })

  // Get selected theme for color defaults
  const selectedThemeSlug = form.watch('website_theme_id')
  const selectedTheme = themes.find((t) => t.slug === selectedThemeSlug)

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        website_enabled: settings[WEBSITE_SETTINGS.ENABLED] === 'true',
        website_theme_id: settings[WEBSITE_SETTINGS.THEME_ID] || '',
        website_primary_color: settings[WEBSITE_SETTINGS.PRIMARY_COLOR] || '',
        website_secondary_color:
          settings[WEBSITE_SETTINGS.SECONDARY_COLOR] || '',
        website_accent_color: settings[WEBSITE_SETTINGS.ACCENT_COLOR] || '',
        seo_meta_title: settings[WEBSITE_SETTINGS.META_TITLE] || '',
        seo_meta_description: settings[WEBSITE_SETTINGS.META_DESCRIPTION] || '',
        seo_google_analytics_id:
          settings[WEBSITE_SETTINGS.GOOGLE_ANALYTICS_ID] || '',
        website_custom_head_scripts:
          settings[WEBSITE_SETTINGS.CUSTOM_HEAD_SCRIPTS] || '',
        website_custom_body_scripts:
          settings[WEBSITE_SETTINGS.CUSTOM_BODY_SCRIPTS] || '',
      })
    }
  }, [settings, form])

  // Save mutation
  const mutation = useMutation({
    mutationFn: async (data: WebsiteSettingsFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      // Build settings to upsert
      const settingsToSave = [
        {
          key: WEBSITE_SETTINGS.ENABLED,
          value: data.website_enabled ? 'true' : 'false',
          description: 'Website Enabled',
        },
        {
          key: WEBSITE_SETTINGS.THEME_ID,
          value: data.website_theme_id || null,
          description: 'Selected Theme Slug',
        },
        {
          key: WEBSITE_SETTINGS.PRIMARY_COLOR,
          value: data.website_primary_color || null,
          description: 'Primary Color Override',
        },
        {
          key: WEBSITE_SETTINGS.SECONDARY_COLOR,
          value: data.website_secondary_color || null,
          description: 'Secondary Color Override',
        },
        {
          key: WEBSITE_SETTINGS.ACCENT_COLOR,
          value: data.website_accent_color || null,
          description: 'Accent Color Override',
        },
        {
          key: WEBSITE_SETTINGS.META_TITLE,
          value: data.seo_meta_title || null,
          description: 'SEO Meta Title',
        },
        {
          key: WEBSITE_SETTINGS.META_DESCRIPTION,
          value: data.seo_meta_description || null,
          description: 'SEO Meta Description',
        },
        {
          key: WEBSITE_SETTINGS.GOOGLE_ANALYTICS_ID,
          value: data.seo_google_analytics_id || null,
          description: 'Google Analytics ID',
        },
        {
          key: WEBSITE_SETTINGS.CUSTOM_HEAD_SCRIPTS,
          value: data.website_custom_head_scripts || null,
          description: 'Custom Header Scripts',
        },
        {
          key: WEBSITE_SETTINGS.CUSTOM_BODY_SCRIPTS,
          value: data.website_custom_body_scripts || null,
          description: 'Custom Body End Scripts',
        },
      ]

      // Upsert each setting
      for (const setting of settingsToSave) {
        const { error } = await supabase.from('account_settings').upsert(
          {
            account_id: activeAccount.id,
            key: setting.key,
            value: setting.value,
            description: setting.description,
            is_secret: false,
          },
          { onConflict: 'account_id,key' }
        )

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['account-settings', activeAccount?.id],
      })
      toast({ title: 'Website settings saved' })
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  // Copy URL to clipboard
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicSiteUrl)
      setCopied(true)
      toast({ title: 'URL copied to clipboard' })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({ title: 'Error', description: 'Failed to copy URL', variant: 'destructive' })
    }
  }

  // Preview site in new tab
  const previewSite = () => {
    window.open(publicSiteUrl, '_blank')
  }

  // Reset color to theme default
  const resetColor = (
    field: 'website_primary_color' | 'website_secondary_color' | 'website_accent_color'
  ) => {
    form.setValue(field, '')
  }

  const isLoading = settingsLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
        className="space-y-6"
      >
        {/* Website Status Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Website Status
            </CardTitle>
            <CardDescription>
              Control your public website visibility
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Toggle */}
            <FormField
              control={form.control}
              name="website_enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Publish Website</FormLabel>
                    <FormDescription>
                      Make your website publicly accessible
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Website URL */}
            {activeAccount?.slug && (
              <div className="space-y-2">
                <Label>Your Website URL</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-md border bg-muted px-3 py-2">
                    <code className="text-sm">{publicSiteUrl}</code>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copyUrl}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={previewSite}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  {form.watch('website_enabled')
                    ? 'Your website is live and accessible'
                    : 'Enable your website to make it publicly accessible'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Theme Selection Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Theme
            </CardTitle>
            <CardDescription>
              Choose a design theme for your website
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="website_theme_id"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {themes.map((theme) => (
                        <div
                          key={theme.slug}
                          onClick={() => field.onChange(theme.slug)}
                          className={cn(
                            'relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:border-primary/50',
                            field.value === theme.slug
                              ? 'border-primary bg-primary/5'
                              : 'border-border'
                          )}
                        >
                          {/* Theme Preview Image */}
                          <div className="mb-3 aspect-video overflow-hidden rounded-md bg-muted">
                            <div
                              className="flex h-full w-full items-center justify-center"
                              style={{
                                background: `linear-gradient(135deg, ${theme.defaults.primary_color || '#C9A227'} 0%, ${theme.defaults.secondary_color || '#1A1A1A'} 100%)`,
                              }}
                            >
                              <span className="text-2xl font-bold text-white">
                                {theme.name.charAt(0)}
                              </span>
                            </div>
                          </div>

                          {/* Theme Info */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">{theme.name}</h4>
                              {theme.slug === 'default' && (
                                <Badge variant="secondary" className="text-xs">
                                  Default
                                </Badge>
                              )}
                            </div>
                            {theme.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {theme.description}
                              </p>
                            )}
                            {/* Color Swatches */}
                            <div className="flex gap-1 pt-2">
                              <div
                                className="h-4 w-4 rounded-full border"
                                style={{
                                  backgroundColor:
                                    theme.defaults.primary_color || '#C9A227',
                                }}
                                title="Primary"
                              />
                              <div
                                className="h-4 w-4 rounded-full border"
                                style={{
                                  backgroundColor:
                                    theme.defaults.secondary_color || '#1A1A1A',
                                }}
                                title="Secondary"
                              />
                              <div
                                className="h-4 w-4 rounded-full border"
                                style={{
                                  backgroundColor:
                                    theme.defaults.accent_color || '#FFFFFF',
                                }}
                                title="Accent"
                              />
                            </div>
                          </div>

                          {/* Selected Indicator */}
                          {field.value === theme.slug && (
                            <div className="absolute right-2 top-2">
                              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                                <Check className="h-3 w-3 text-primary-foreground" />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {themes?.length === 0 && (
              <div className="py-8 text-center text-muted-foreground">
                No themes available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Color Customization Section */}
        {selectedTheme && (
          <Card>
            <CardHeader>
              <CardTitle>Color Customization</CardTitle>
              <CardDescription>
                Override the default theme colors (leave blank to use theme
                defaults)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-3">
                {/* Primary Color */}
                <FormField
                  control={form.control}
                  name="website_primary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Color</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <div className="flex flex-1 items-center gap-2">
                            <input
                              type="color"
                              value={
                                field.value ||
                                selectedTheme.defaults.primary_color ||
                                '#C9A227'
                              }
                              onChange={(e) => field.onChange(e.target.value)}
                              className="h-10 w-14 cursor-pointer rounded border bg-transparent p-1"
                            />
                            <Input
                              placeholder={
                                selectedTheme.defaults.primary_color || '#C9A227'
                              }
                              {...field}
                              className="font-mono uppercase"
                            />
                          </div>
                        </FormControl>
                        {field.value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => resetColor('website_primary_color')}
                            title="Reset to default"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Secondary Color */}
                <FormField
                  control={form.control}
                  name="website_secondary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Color</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <div className="flex flex-1 items-center gap-2">
                            <input
                              type="color"
                              value={
                                field.value ||
                                selectedTheme.defaults.secondary_color ||
                                '#1A1A1A'
                              }
                              onChange={(e) => field.onChange(e.target.value)}
                              className="h-10 w-14 cursor-pointer rounded border bg-transparent p-1"
                            />
                            <Input
                              placeholder={
                                selectedTheme.defaults.secondary_color ||
                                '#1A1A1A'
                              }
                              {...field}
                              className="font-mono uppercase"
                            />
                          </div>
                        </FormControl>
                        {field.value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              resetColor('website_secondary_color')
                            }
                            title="Reset to default"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Accent Color */}
                <FormField
                  control={form.control}
                  name="website_accent_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accent Color</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <div className="flex flex-1 items-center gap-2">
                            <input
                              type="color"
                              value={
                                field.value ||
                                selectedTheme.defaults.accent_color ||
                                '#FFFFFF'
                              }
                              onChange={(e) => field.onChange(e.target.value)}
                              className="h-10 w-14 cursor-pointer rounded border bg-transparent p-1"
                            />
                            <Input
                              placeholder={
                                selectedTheme.defaults.accent_color || '#FFFFFF'
                              }
                              {...field}
                              className="font-mono uppercase"
                            />
                          </div>
                        </FormControl>
                        {field.value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => resetColor('website_accent_color')}
                            title="Reset to default"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* SEO Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              SEO Settings
            </CardTitle>
            <CardDescription>
              Optimize your website for search engines
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Meta Title */}
            <FormField
              control={form.control}
              name="seo_meta_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meta Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={`${activeAccount?.name || 'Your Business'} | Luxury Transportation`}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The title that appears in search results and browser tabs
                    (max 70 characters)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Meta Description */}
            <FormField
              control={form.control}
              name="seo_meta_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meta Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Professional limousine and luxury transportation services for all occasions..."
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    A brief description of your business for search results (max
                    160 characters)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Google Analytics */}
            <FormField
              control={form.control}
              name="seo_google_analytics_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Google Analytics ID</FormLabel>
                  <FormControl>
                    <Input placeholder="G-XXXXXXXXXX" {...field} />
                  </FormControl>
                  <FormDescription>
                    Your Google Analytics 4 measurement ID to track website
                    traffic
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Custom Scripts Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5" />
              Custom Scripts
            </CardTitle>
            <CardDescription>
              Add custom HTML, JavaScript, or tracking pixels to your website
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Security Warning */}
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Security Warning</AlertTitle>
              <AlertDescription>
                Only add scripts from trusted sources. Malicious scripts can
                compromise your website and visitor data. If you're unsure about
                a script, consult with a developer first.
              </AlertDescription>
            </Alert>

            {/* Header Scripts */}
            <FormField
              control={form.control}
              name="website_custom_head_scripts"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Header Scripts</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={`<!-- Example: Meta Pixel -->\n<script>\n  !function(f,b,e,v,n,t,s)\n  {if(f.fbq)return;n=f.fbq=function()...\n</script>`}
                      className="min-h-[150px] resize-y font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Scripts and tags to inject in the &lt;head&gt; section.
                    Useful for tracking pixels, meta tags, and stylesheets.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Body End Scripts */}
            <FormField
              control={form.control}
              name="website_custom_body_scripts"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Body End Scripts</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={`<!-- Example: Chat Widget -->\n<script src="https://widget.example.com/chat.js"></script>\n<script>\n  ChatWidget.init({ key: 'your-key' });\n</script>`}
                      className="min-h-[150px] resize-y font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Scripts to inject before the closing &lt;/body&gt; tag.
                    Useful for chat widgets, analytics scripts, and deferred
                    loading.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending} size="lg">
            {mutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Settings
          </Button>
        </div>
      </form>
    </Form>
  )
}
